/**
 * AdminTenantDatabase — przeglądarka bazy tenanta dla wdrożeniowców (panel ITE, tylko odczyt).
 *
 * Route: /tenants/:tenantId/database (otwierana przyciskiem „Przeglądarka bazy" z karty tenanta).
 * Układ: Eksplorator obiektów (drzewo schematu tenanta) | zakładki: Właściwości / SQL /
 * Statystyki / Zależności wybranego obiektu + dowolna liczba zakładek Query Tool.
 * Zapytania: tylko SELECT / WITH / EXPLAIN — pilnuje tego serwer (parser + rola tylko do
 * odczytu); front pokazuje komunikaty i podkreśla miejsce błędu.
 */

import { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Maximize2, Plus, ShieldCheck, X } from 'lucide-react';

import { Alert } from '@/components/Alert';
import { Button } from '@/components/Button';
import { ObjectDetailsPanel, type ObjectPanel } from '@/components/db-browser/ObjectDetailsPanel';
import { type ExplorerAction, ObjectExplorer } from '@/components/db-browser/ObjectExplorer';
import { QueryTool } from '@/components/db-browser/QueryTool';
import { IconButton } from '@/components/IconButton';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { Skeleton } from '@/components/Skeleton';
import { useTabsOptional } from '@/contexts/TabContext';
import { useToast } from '@/contexts/ToastContext';
import { fetchDbSchema } from '@/lib/db-browser/api';
import { selectScript, viewDataSql } from '@/lib/db-browser/scripts';
import { completionSchema, type DbTreeNode } from '@/lib/db-browser/tree';
import type { DbObjectRef, DbQueryLimit, DbRelation } from '@/lib/db-browser/types';
import { cn } from '@/lib/utils';

interface QueryTab {
  id: string;
  title: string;
  sql: string;
  autoRun?: boolean;
  limit?: DbQueryLimit;
}

type ActiveTab = ObjectPanel | `q:${string}`;

const OBJECT_PANELS: ObjectPanel[] = ['props', 'sql', 'stats', 'deps'];
const tabsKey = (tenantId: string) => `saas_db_browser_tabs:${tenantId}`;

function readTabs(tenantId: string): QueryTab[] {
  try {
    const raw = localStorage.getItem(tabsKey(tenantId));
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (Array.isArray(parsed) && parsed.length) {
      return (parsed as QueryTab[]).map((tab) => ({ id: tab.id, title: tab.title, sql: tab.sql }));
    }
  } catch {
    // ignorujemy uszkodzony wpis
  }
  return [];
}

let seq = 0;
const newId = () => `${Date.now().toString(36)}${(seq++).toString(36)}`;

export function AdminTenantDatabase() {
  const { t } = useTranslation();
  const toast = useToast();
  const { tenantId = '' } = useParams<{ tenantId: string }>();

  const {
    data: snapshot,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['admin', 'tenant-db', tenantId, 'schema'],
    queryFn: () => fetchDbSchema(tenantId),
    enabled: !!tenantId,
    staleTime: 5 * 60_000,
    retry: false,
  });

  const [selectedNode, setSelectedNode] = useState<DbTreeNode | null>(null);
  const [fullscreen, setFullscreen] = useState(false);

  // Esc zamyka pełny ekran — chyba że Esc obsłużył już ktoś inny (menu, lista, okno)
  // albo fokus jest w edytorze SQL (tam Esc zamyka podpowiadacz).
  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if ((e.target as HTMLElement | null)?.closest?.('.cm-editor')) return;
      setFullscreen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [fullscreen]);
  const [queryTabs, setQueryTabs] = useState<QueryTab[]>(() => {
    const stored = readTabs(tenantId);
    return stored.length
      ? stored
      : [{ id: newId(), title: t('admin.dbBrowser.tabs.queryN', { n: 1 }), sql: '' }];
  });
  const [active, setActive] = useState<ActiveTab>(() => `q:${queryTabs[0]!.id}`);
  const [filterFor, setFilterFor] = useState<DbRelation | null>(null);
  const titleCounter = useRef(queryTabs.length);

  useEffect(() => {
    try {
      localStorage.setItem(
        tabsKey(tenantId),
        JSON.stringify(queryTabs.map(({ id, title, sql }) => ({ id, title, sql }))),
      );
    } catch {
      // brak miejsca — zakładki nie przetrwają odświeżenia, praca trwa
    }
  }, [queryTabs, tenantId]);

  // Etykieta karty powłoki: „Baza: <nazwa tenanta>" zamiast ogólnej z ścieżki.
  const tabCtx = useTabsOptional();
  useEffect(() => {
    if (!tabCtx || !snapshot) return;
    const myTab = tabCtx.tabs.find((tt) => tt.basePath === `/tenants/${tenantId}/database`);
    const label = t('admin.dbBrowser.tabLabel', { name: snapshot.tenant.name });
    if (myTab && myTab.label !== label) tabCtx.updateTabLabel(myTab.id, label);
  }, [tabCtx, snapshot, tenantId, t]);

  const schemaForCompletion = useMemo(
    () => (snapshot ? completionSchema(snapshot) : {}),
    [snapshot],
  );

  const openQueryTab = useCallback(
    (opts: { sql?: string; title?: string; autoRun?: boolean; limit?: DbQueryLimit }) => {
      titleCounter.current += 1;
      const tab: QueryTab = {
        id: newId(),
        title: opts.title ?? t('admin.dbBrowser.tabs.queryN', { n: titleCounter.current }),
        sql: opts.sql ?? '',
        autoRun: opts.autoRun,
        limit: opts.limit,
      };
      setQueryTabs((prev) => [...prev, tab]);
      setActive(`q:${tab.id}`);
    },
    [t],
  );

  const closeQueryTab = (id: string) => {
    const next = queryTabs.filter((tab) => tab.id !== id);
    setQueryTabs(next);
    if (active === `q:${id}`) {
      const fallback = next[next.length - 1];
      setActive(fallback ? `q:${fallback.id}` : 'props');
    }
  };

  const copy = useCallback(
    async (text: string) => {
      try {
        await navigator.clipboard.writeText(text);
        toast.show({ variant: 'success', title: t('admin.dbBrowser.copied') });
      } catch {
        toast.show({ variant: 'error', title: t('admin.dbBrowser.copyFailed') });
      }
    },
    [t, toast],
  );

  const relationOf = (node: DbTreeNode): DbRelation | undefined =>
    node.ref && snapshot ? snapshot.relations.find((r) => r.name === node.ref!.name) : undefined;

  const onAction = (action: ExplorerAction, node: DbTreeNode) => {
    if (!snapshot) return;
    const rel = relationOf(node);
    const name = rel?.name ?? node.label;
    switch (action) {
      case 'query_tool':
        openQueryTab({});
        return;
      case 'view_first':
      case 'view_last':
      case 'view_all':
        if (!rel) return;
        openQueryTab({
          sql: viewDataSql(
            snapshot.schema,
            rel,
            action === 'view_first' ? 'first' : action === 'view_last' ? 'last' : 'all',
          ),
          title: t('admin.dbBrowser.tabs.dataOf', { name }),
          autoRun: true,
          limit: action === 'view_all' ? 1000 : 100,
        });
        return;
      case 'view_filter':
        if (rel) setFilterFor(rel);
        return;
      case 'script_select':
        if (rel) openQueryTab({ sql: selectScript(snapshot.schema, rel), title: name });
        return;
      case 'script_create':
        setSelectedNode(node);
        setActive('sql');
        return;
      case 'properties':
        setSelectedNode(node);
        setActive('props');
        return;
      case 'copy_name':
        void copy(node.insertText ?? node.label);
        return;
    }
  };

  const objectRef: DbObjectRef | null = selectedNode?.ref ?? null;

  if (isLoading) {
    return (
      <div className="flex h-full flex-col gap-3 p-6" aria-busy="true">
        <p className="text-sm text-text-tertiary">{t('admin.dbBrowser.loading')}</p>
        <Skeleton height={28} />
        <Skeleton height={400} />
      </div>
    );
  }

  if (isError || !snapshot) {
    const status = axios.isAxiosError(error) ? error.response?.status : undefined;
    const key =
      status === 403 ? 'forbidden' : status === 404 || status === 409 ? 'unavailable' : 'generic';
    return (
      <div className="p-6">
        <Link to={`/tenants/${tenantId}`} className="text-sm text-accent-600 hover:text-accent-700">
          ← {t('admin.dbBrowser.backToTenant')}
        </Link>
        <div className="mt-4 max-w-2xl">
          <Alert tone="error" title={t(`admin.dbBrowser.loadError.${key}.title`)}>
            {t(`admin.dbBrowser.loadError.${key}.body`)}
          </Alert>
        </div>
      </div>
    );
  }

  const objectTabLabel: Record<ObjectPanel, string> = {
    props: t('admin.dbBrowser.tabs.properties'),
    sql: t('admin.dbBrowser.tabs.sql'),
    stats: t('admin.dbBrowser.tabs.statistics'),
    deps: t('admin.dbBrowser.tabs.dependencies'),
  };
  const contextLabel = `${snapshot.schema}@${snapshot.tenant.code}`;

  return (
    <div
      data-testid="db-browser-root"
      className={cn(
        'flex flex-col bg-bg-primary',
        // Pełny ekran: nad menu bocznym (z-45), pod oknami modalnymi (z-50) i toastami (z-60).
        fullscreen ? 'fixed inset-0 z-[48]' : 'h-full min-h-[560px]',
      )}
    >
      <header className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-border-secondary px-4 py-2">
        <Link to={`/tenants/${tenantId}`} className="text-xs text-accent-600 hover:text-accent-700">
          ← {snapshot.tenant.name}
        </Link>
        <h1 className="text-[15px] font-semibold text-text-primary">
          {t('admin.dbBrowser.title')}
        </h1>
        <span className="font-mono text-xs text-text-secondary">
          {snapshot.tenant.code} · {snapshot.tenant.dbMode} · {snapshot.database} ·{' '}
          {snapshot.schema}
        </span>
        <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-semibold text-green-800 dark:bg-green-900/40 dark:text-green-300">
          <ShieldCheck size={12} aria-hidden="true" />
          {t('admin.dbBrowser.readOnlyBadge')}
        </span>
        <div className="flex-1" />
        <span className="text-xs text-text-tertiary">{t('admin.dbBrowser.auditNote')}</span>
        {fullscreen ? (
          <IconButton
            size="sm"
            aria-label={t('admin.dbBrowser.fullscreen.exit')}
            title={t('admin.dbBrowser.fullscreen.exit')}
            onClick={() => setFullscreen(false)}
          >
            <X />
          </IconButton>
        ) : (
          <IconButton
            size="sm"
            aria-label={t('admin.dbBrowser.fullscreen.enter')}
            title={t('admin.dbBrowser.fullscreen.enter')}
            onClick={() => setFullscreen(true)}
          >
            <Maximize2 />
          </IconButton>
        )}
      </header>

      <div className="flex min-h-0 flex-1">
        <div className="w-[320px] shrink-0">
          <ObjectExplorer
            snapshot={snapshot}
            selectedId={selectedNode?.id ?? null}
            onSelect={(node) => {
              setSelectedNode(node);
              if (node.ref && active.startsWith('q:')) setActive('props');
            }}
            onAction={onAction}
            onRefresh={() => void refetch()}
            refreshing={isFetching}
          />
        </div>

        <section
          aria-label={t('admin.dbBrowser.workspace')}
          className="flex min-w-0 flex-1 flex-col"
        >
          <div
            role="tablist"
            aria-label={t('admin.dbBrowser.tabs.label')}
            className="flex h-9 shrink-0 items-end overflow-x-auto border-b border-border-secondary bg-bg-secondary px-1"
          >
            {OBJECT_PANELS.map((p) => (
              <Button
                key={p}
                variant="ghost"
                role="tab"
                aria-selected={active === p}
                onClick={() => setActive(p)}
                className={cn(
                  'h-[34px] shrink-0 rounded-none border-t-2 px-3.5 text-[13px]',
                  active === p
                    ? 'border-accent-600 bg-bg-primary font-semibold'
                    : 'border-transparent font-normal text-text-secondary',
                )}
              >
                {objectTabLabel[p]}
              </Button>
            ))}
            <span aria-hidden="true" className="mx-1 mb-2 h-4 w-px shrink-0 bg-border-secondary" />
            {queryTabs.map((tab) => {
              const isActive = active === `q:${tab.id}`;
              return (
                <div
                  key={tab.id}
                  className={cn(
                    'flex h-[34px] shrink-0 items-center border-t-2',
                    isActive ? 'border-accent-600 bg-bg-primary' : 'border-transparent',
                  )}
                >
                  <Button
                    variant="ghost"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => setActive(`q:${tab.id}`)}
                    className={cn(
                      'h-full max-w-[220px] rounded-none pl-3.5 pr-1 text-[13px] hover:bg-transparent',
                      isActive ? 'font-semibold' : 'font-normal text-text-secondary',
                    )}
                  >
                    <span className="truncate">{tab.title}</span>
                  </Button>
                  <IconButton
                    size="sm"
                    aria-label={t('admin.dbBrowser.tabs.close', { name: tab.title })}
                    onClick={() => closeQueryTab(tab.id)}
                    className="mr-1 h-5 w-5 [&_svg]:h-3 [&_svg]:w-3"
                  >
                    <X />
                  </IconButton>
                </div>
              );
            })}
            <IconButton
              size="sm"
              aria-label={t('admin.dbBrowser.tabs.newQuery')}
              title={t('admin.dbBrowser.tabs.newQuery')}
              onClick={() => openQueryTab({})}
              className="mb-1 ml-1 shrink-0"
            >
              <Plus />
            </IconButton>
          </div>

          <div className="relative min-h-0 flex-1">
            {OBJECT_PANELS.includes(active as ObjectPanel) && (
              <ObjectDetailsPanel
                tenantId={tenantId}
                objectRef={objectRef}
                panel={active as ObjectPanel}
                onCopy={(text) => void copy(text)}
                onOpenInQueryTool={(sql) => openQueryTab({ sql, title: selectedNode?.label })}
              />
            )}
            {/* Zakładki zapytań żyją w tle (wynik zostaje po przełączeniu). */}
            {queryTabs.map((tab) => (
              <div
                key={tab.id}
                className="absolute inset-0"
                style={{ display: active === `q:${tab.id}` ? 'block' : 'none' }}
              >
                <QueryTool
                  tenantId={tenantId}
                  sql={tab.sql}
                  onSqlChange={(sql) =>
                    setQueryTabs((prev) => prev.map((x) => (x.id === tab.id ? { ...x, sql } : x)))
                  }
                  completionSchema={schemaForCompletion}
                  contextLabel={contextLabel}
                  autoRun={tab.autoRun}
                  initialLimit={tab.limit}
                />
              </div>
            ))}
          </div>
        </section>
      </div>

      {filterFor && (
        <FilterRowsModal
          relation={filterFor}
          onClose={() => setFilterFor(null)}
          onSubmit={(where) => {
            openQueryTab({
              sql: viewDataSql(snapshot.schema, filterFor, 'filter', where),
              title: t('admin.dbBrowser.tabs.dataOf', { name: filterFor.name }),
              autoRun: true,
              limit: 1000,
            });
            setFilterFor(null);
          }}
        />
      )}
    </div>
  );
}

function FilterRowsModal({
  relation,
  onClose,
  onSubmit,
}: {
  relation: DbRelation;
  onClose: () => void;
  onSubmit: (where: string) => void;
}) {
  const { t } = useTranslation();
  const [where, setWhere] = useState('');
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (where.trim()) onSubmit(where);
  };
  return (
    <Modal
      open
      onClose={onClose}
      title={t('admin.dbBrowser.filter.title', { name: relation.name })}
      description={t('admin.dbBrowser.filter.description')}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('admin.dbBrowser.filter.cancel')}
          </Button>
          <Button type="submit" form="db-filter-form" disabled={!where.trim()}>
            {t('admin.dbBrowser.filter.submit')}
          </Button>
        </>
      }
    >
      <form id="db-filter-form" onSubmit={submit}>
        <Input
          label={t('admin.dbBrowser.filter.label')}
          value={where}
          onChange={(e) => setWhere(e.target.value)}
          placeholder={t('admin.dbBrowser.filter.placeholder')}
          className="font-mono"
          autoFocus
        />
      </form>
    </Modal>
  );
}
